import React, {useCallback, useEffect, useRef, useState} from 'react';
import {View} from 'react-native';
import {observer} from 'mobx-react-lite';

import BackButton from '../../components/rewards/BackButton';
import RewardsProgramImage from '../../components/rewards/ProgramImage';
import RewardsCard from '../../components/rewards/RewardsCard';
import RewardsLink from '../../components/rewards/RewardsLink';
import StaffStorePicker from '../../components/StaffStorePicker';
import StoreItem from '../../components/StoreItem';
import Button from '../../components/themed/Button';
import KeyboardAwareScrollView from '../../components/themed/KeyboardAwareScrollView';
import Text from '../../components/themed/Text';
import ContentView from '../../components/ui-controls/layout/ContentView';
import {AppRoute} from '../../models/AppRoute';
import {Shop} from '../../models/middleware';
import {RewardsSignUpTypeParams, RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {navigate} from '../../navigation/RootNavigation';
import {useStores} from '../../stores/use-stores';
import {classifyStaffConflict, isStaffActivationBlocked} from '../../utils/staffActivation';

import {AccountStatus} from './types';

const StaffStoreSelectionScreen = observer(
  ({route, navigation}: RouteNavigatorScreenProps<AppRoute.REWARDS_STAFF_STORE_SELECTION>) => {
    const {rewardsStore} = useStores();
    const {flowType} = route.params || ({} as RewardsSignUpTypeParams);
    const [selectedStore, setSelectedStore] = useState<Shop | null>(null);
    const [signUpError, setSignUpError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const mountedRef = useRef(true);

    useEffect(() => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
      };
    }, []);

    const dayName = new Date().toLocaleString('en-AU', {weekday: 'long'}).toLowerCase();

    const handleStoreSelected = useCallback(
      (shop: Shop) => {
        setSelectedStore(shop);
        rewardsStore.setStaffStoreId(shop.store_number);
        rewardsStore.setStaffStore(shop);
      },
      [rewardsStore],
    );

    const handleChangeStore = useCallback(() => {
      setSelectedStore(null);
      rewardsStore.setStaffStoreId(null);
      rewardsStore.setStaffStore(null);
    }, [rewardsStore]);

    // DIS-1185: explicit in-app escape hatch from the bad-code error state.
    const handleReturnToStart = (reason?: 'verification-expired') => {
      setSignUpError(null);
      setSelectedStore(null);
      rewardsStore.setStaffStoreId(null);
      rewardsStore.setStaffStore(null);
      navigate(AppRoute.REWARDS_VERIFICATION, {
        flowType: flowType || AccountStatus.NO_LITE_SIGNUP,
        ...(reason ? {returnReason: reason} : {}),
      });
    };

    // DIS-1190: phase 3 — backend looks up the parked workflow by
    // (tenant, E.164 phone). 410 session_expired = workflow TTL elapsed
    // (>5min since phase 1) → restart from phase 1. 400 invalid_store_id
    // = staff store not in directory; surface inline so the user can
    // re-pick. Generic failures show the existing error UI with RETURN
    // TO START button.
    const handleContinue = async () => {
      if (!selectedStore || isSubmitting) return;
      // DIS-1190: phase 3 needs the SignUp.tsx form fields for SIGNUP_LITE
      // (new user). ACTIVATE_REWARDS / NO_LITE_SIGNUP (existing user
      // activation) skips SignUp.tsx — backend reuses the existing Zen
      // card data. signUpCode is optional for staff (?signUpCode= URL
      // param when present, omitted otherwise).
      const isNewSignup = flowType === AccountStatus.SIGNUP_LITE;
      if (
        isNewSignup &&
        (!rewardsStore.firstName ||
          !rewardsStore.lastName ||
          !rewardsStore.emailAddress ||
          !rewardsStore.dateOfBirth ||
          !rewardsStore.suburb)
      ) {
        setSignUpError(
          'We need a few more details before we can finish. Please go back and complete sign up.',
        );
        return;
      }
      setSignUpError(null);
      setIsSubmitting(true);
      try {
        const result = await rewardsStore.signUpStaff(flowType || AccountStatus.NO_LITE_SIGNUP);
        if (!mountedRef.current) return;
        if (result.ok) {
          // DIS-1329: staff promotion blocked — block here too (store-picker
          // path) and divert to the terminal screen instead of Success.
          if (isStaffActivationBlocked(result.data)) {
            navigate(AppRoute.REWARDS_STAFF_ACTIVATION_BLOCKED, {
              conflictKind: classifyStaffConflict(result.data.staffActivation?.reason),
            });
            return;
          }
          navigate(AppRoute.REWARDS_SUCCESS, {flowType});
          return;
        }
        switch (result.reason) {
          case 'account_not_found':
            // DIS-1328: 422 — no CRM contact for this phone. Terminal, no retry.
            navigate(AppRoute.REWARDS_ACCOUNT_NOT_FOUND);
            break;
          case 'session_expired':
            navigate(AppRoute.REWARDS_VERIFICATION, {
              flowType: flowType || AccountStatus.NO_LITE_SIGNUP,
              returnReason: 'verification-expired',
            });
            break;
          case 'invalid_store_id':
            setSelectedStore(null);
            rewardsStore.setStaffStoreId(null);
            rewardsStore.setStaffStore(null);
            setSignUpError('That store is not recognised. Please pick a different store.');
            break;
          case 'service':
          case 'network':
            setSignUpError('Service temporarily unavailable. Please try again shortly.');
            break;
          case 'phone_invalid':
          case 'workflow_failed':
          case 'unknown':
          default:
            setSignUpError('Something went wrong. Please try again.');
            break;
        }
      } catch (_e) {
        if (!mountedRef.current) return;
        setSignUpError('Something went wrong. Please try again.');
      } finally {
        if (mountedRef.current) {
          setIsSubmitting(false);
        }
      }
    };

    return (
      <KeyboardAwareScrollView
        style={{flex: 1}}
        contentContainerStyle={{flex: 1}}
        keyboardShouldPersistTaps="always">
        <ContentView parentStyle={{flex: 1, paddingBottom: 16}} style={{flex: 1}}>
          <BackButton
            onPress={() => {
              // DIS-1343: back returns to the original sign-in entry point
              // (staff/signUpCode preserved) rather than popping to the
              // verification screen, whose phase-1 code is already consumed.
              navigation.navigate(AppRoute.REWARDS_SIGN_IN, rewardsStore.signInRouteParams);
            }}
          />
          <RewardsProgramImage variant="onLight" />

          <View>
            <Text variant="heading1">{selectedStore ? 'YOUR STORE' : 'SELECT YOUR STORE'}</Text>
          </View>
          <Text>
            {selectedStore
              ? 'You have selected the following store as your workplace.'
              : 'Select the store where you work to complete your staff registration.'}
          </Text>

          {selectedStore ? (
            <RewardsCard
              variant="subtle"
              padding="s4"
              style={{marginTop: 16}}
              testID="selected-store-info">
              <StoreItem item={selectedStore} dayName={dayName} />
              <RewardsLink
                onPress={handleChangeStore}
                align="flex-start"
                accessibilityLabel="Change selected store"
                testID="change-store-button"
                style={{marginTop: 8}}>
                Change store
              </RewardsLink>
            </RewardsCard>
          ) : (
            <StaffStorePicker
              mode="full"
              onStoreSelected={handleStoreSelected}
              selectedStore={selectedStore}
              testID="staff-store-picker"
            />
          )}

          <View style={{gap: 8, marginTop: 16}}>
            <Button
              title="CONTINUE"
              onPress={handleContinue}
              disabled={!selectedStore || isSubmitting}
              loading={isSubmitting}
            />
            {signUpError ? (
              <>
                <Text
                  variant="body2"
                  color="error"
                  style={{marginTop: 8}}
                  accessibilityRole="alert"
                  accessibilityLiveRegion="polite"
                  testID="signup-error">
                  {signUpError}
                </Text>
                <Button
                  title="RETURN TO START"
                  variant="outlined"
                  onPress={() => handleReturnToStart()}
                  testID="return-to-start-button"
                />
              </>
            ) : null}
          </View>
        </ContentView>
      </KeyboardAwareScrollView>
    );
  },
);

export default StaffStoreSelectionScreen;
